/**
 * Swiper Progress Bar Module
 * 
 * 可复用的 Swiper 进度条功能模块
 * 支持两种模式：
 * 1. 单进度条模式（每个slide共享一个进度条，如 banner-swiper, product-series-swiper）
 * 2. 多进度条模式（每个slide内部有自己的进度条，如 product-showcase-swiper）
 * 
 * @param {Object} options - 配置选项
 * @param {HTMLElement|string} options.swiperEl - Swiper 容器元素或选择器
 * @param {Object} options.autoplayConfig - Swiper autoplay 配置对象，false 表示禁用
 * @param {Object} options.swiperConfig - Swiper 初始化配置
 * @param {number} options.progressBarWidth - 每个slide的进度条宽度（默认70px）
 * @param {string} options.progressTrackSelector - 进度条轨道选择器（单进度条模式）
 * @param {string} options.progressFillSelector - 进度条填充选择器（单进度条模式）
 * @param {string} options.progressContainerSelector - 进度条容器选择器（单进度条模式）
 * @param {boolean} options.multiProgressMode - 是否为多进度条模式（默认false）
 * @param {string} options.slideProgressTrackSelector - slide内进度条轨道选择器（多进度条模式，默认'.product-showcase-slide__progress-track'）
 * @param {string} options.slideProgressFillSelector - slide内进度条填充选择器（多进度条模式，默认'.product-showcase-slide__progress-fill'）
 * @param {Function} options.onProgressUpdate - 进度更新回调函数
 * @param {Function} options.onSwiperInit - Swiper 初始化完成回调
 */
(function() {
  'use strict';

  function SwiperProgressBar(options) {
    this.options = Object.assign({
      progressBarWidth: 70,
      multiProgressMode: false,
      slideProgressTrackSelector: '.product-showcase-slide__progress-track',
      slideProgressFillSelector: '.product-showcase-slide__progress-fill',
      progressTrackSelector: '.swiper-progress__track',
      progressFillSelector: '.swiper-progress__fill',
      progressContainerSelector: '.swiper-progress',
      onProgressUpdate: null,
      onSwiperInit: null
    }, options);

    this.swiperEl = typeof options.swiperEl === 'string' 
      ? document.querySelector(options.swiperEl) 
      : options.swiperEl;
    
    if (!this.swiperEl) {
      console.warn('Swiper element not found:', options.swiperEl);
      return;
    }

    this.progressInterval = null;
    this.progressStartTime = Date.now();
    this.currentSlideIndex = 0;
    this.swiper = null;
    this.slidesCount = 0;
    this.existingSlides = [];

    this.init();
  }

  SwiperProgressBar.prototype.init = function() {
    const self = this;

    function initSwiperProgress() {
      // 等待 Swiper 库加载
      if (typeof Swiper === 'undefined') {
        setTimeout(initSwiperProgress, 100);
        return;
      }

      // 检查是否有 slides
      const wrapper = self.swiperEl.querySelector('.swiper-wrapper');
      if (!wrapper) {
        console.warn('Swiper wrapper not found');
        return;
      }

      self.existingSlides = wrapper.querySelectorAll('.swiper-slide');
      if (self.existingSlides.length === 0) {
        console.warn('No slides found in swiper');
        return;
      }

      self.slidesCount = self.existingSlides.length;
      const totalWidth = self.slidesCount * self.options.progressBarWidth;

      if (self.options.multiProgressMode) {
        // 多进度条模式：为每个slide的进度条设置总宽度
        self.existingSlides.forEach(function(slide) {
          const slideProgressTrack = slide.querySelector(self.options.slideProgressTrackSelector);
          const slideProgressFill = slide.querySelector(self.options.slideProgressFillSelector);
          if (slideProgressTrack && slideProgressFill) {
            slideProgressTrack.style.width = `${totalWidth}px`;
            slideProgressFill.style.width = '0px';
            slideProgressFill.style.transform = 'translateX(0px)';
          }
        });
      } else {
        // 单进度条模式：设置进度条总宽度
        const progressFill = self.swiperEl.querySelector(self.options.progressFillSelector);
        const progressTrack = self.swiperEl.querySelector(self.options.progressTrackSelector);
        const progressContainer = self.swiperEl.querySelector(self.options.progressContainerSelector);

        if (self.slidesCount === 0) {
          if (progressContainer) {
            progressContainer.style.display = 'none';
          }
        } else if (progressTrack && progressFill && progressContainer) {
          progressContainer.style.display = 'flex';
          progressTrack.style.width = `${totalWidth}px`;
          progressFill.style.width = '0px';
          progressFill.style.transform = 'translateX(0px)';
        }
      }

      // 创建 Swiper 实例
      // 注意：如果使用进度条，我们禁用 Swiper 原生的 autoplay，由进度条控制切换
      const swiperConfig = Object.assign({
        effect: 'slide',
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 0,
        watchOverflow: true,
        normalizeSlideIndex: true,
        autoplay: false, // 禁用 Swiper 原生 autoplay，由进度条控制
        on: {
          init: function(swiper) {
            const index = swiper.realIndex !== undefined ? swiper.realIndex : 0;
            self.updateProgress(index);
            if (self.options.autoplayConfig) {
              self.startProgressAnimation(index);
            }
            if (self.options.onSwiperInit) {
              self.options.onSwiperInit(swiper);
            }
          },
          slideChange: function(swiper) {
            const index = swiper.realIndex !== undefined && swiper.realIndex >= 0 
              ? swiper.realIndex 
              : (swiper.activeIndex % self.slidesCount);
            
            // 确保索引在有效范围内
            const safeIndex = Math.max(0, Math.min(index, self.slidesCount - 1));
            
            if (self.progressInterval) {
              clearInterval(self.progressInterval);
            }
            self.updateProgress(safeIndex);
            if (self.options.autoplayConfig) {
              setTimeout(function() {
                self.startProgressAnimation(safeIndex);
              }, 50);
            }
          },
          touchEnd: function() {
            // 延迟更新，确保swiper完成slide切换
            setTimeout(function() {
              const activeIndex = self.swiper.realIndex !== undefined && self.swiper.realIndex >= 0 
                ? self.swiper.realIndex 
                : (self.swiper.activeIndex % self.slidesCount);
              const safeIndex = Math.max(0, Math.min(activeIndex, self.slidesCount - 1));
              if (self.progressInterval) {
                clearInterval(self.progressInterval);
              }
              self.updateProgress(safeIndex);
              if (self.options.autoplayConfig) {
                self.startProgressAnimation(safeIndex);
              }
            }, 100);
          },
          autoplayStart: function() {
            const activeIndex = self.swiper.realIndex !== undefined && self.swiper.realIndex >= 0 
              ? self.swiper.realIndex 
              : (self.swiper.activeIndex % self.slidesCount);
            const safeIndex = Math.max(0, Math.min(activeIndex, self.slidesCount - 1));
            self.startProgressAnimation(safeIndex);
          },
          autoplayStop: function() {
            if (self.progressInterval) {
              clearInterval(self.progressInterval);
            }
          }
        }
      }, self.options.swiperConfig || {});
      
      // 强制禁用 autoplay，因为我们要用进度条控制
      swiperConfig.autoplay = false;

      try {
        self.swiper = new Swiper(self.swiperEl, swiperConfig);
      } catch (error) {
        console.error('Error initializing swiper:', error);
        return;
      }

      // 初始化进度条
      if (self.slidesCount > 0) {
        const initialIndex = self.swiper.realIndex !== undefined && self.swiper.realIndex >= 0 
          ? self.swiper.realIndex 
          : 0;
        self.updateProgress(initialIndex);
        if (self.options.autoplayConfig) {
          // 延迟开始动画，确保swiper完全初始化
          setTimeout(() => {
            self.startProgressAnimation(initialIndex);
          }, 100);
        }
      }
    }

    // 确保 DOM 完全加载后再初始化
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(initSwiperProgress, 100);
      });
    } else {
      setTimeout(initSwiperProgress, 100);
    }
  };

  SwiperProgressBar.prototype.updateProgress = function(activeIndex) {
    if (this.slidesCount === 0) return;
    const realIndex = activeIndex % this.slidesCount;

    if (this.options.multiProgressMode) {
      // 多进度条模式
      const currentSlide = this.existingSlides[realIndex];
      if (currentSlide) {
        const slideProgressFill = currentSlide.querySelector(this.options.slideProgressFillSelector);
        if (slideProgressFill) {
          const currentProgress = realIndex * this.options.progressBarWidth;
          slideProgressFill.style.width = '0px';
          slideProgressFill.style.transform = `translateX(${currentProgress}px)`;
        }
      }
    } else {
      // 单进度条模式
      const progressFill = this.swiperEl.querySelector(this.options.progressFillSelector);
      if (!progressFill) return;
      const currentProgress = realIndex * this.options.progressBarWidth;
      progressFill.style.width = '0px';
      progressFill.style.transform = `translateX(${currentProgress}px)`;
    }

    this.currentSlideIndex = realIndex;

    if (this.options.onProgressUpdate) {
      this.options.onProgressUpdate(realIndex, this.currentSlideIndex);
    }
  };

  SwiperProgressBar.prototype.startProgressAnimation = function(slideIndex) {
    if (!this.options.autoplayConfig) return;

    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }

    const delay = this.options.autoplayConfig.delay || 3000;
    this.progressStartTime = Date.now();

    // 根据当前 swiper 参数动态计算每个进度条宽度
    const swiperInstance = this.swiper;
    const slidesPerView = swiperInstance && swiperInstance.params ? swiperInstance.params.slidesPerView : 1;
    const effectiveSlidesCount = Math.ceil(this.slidesCount / slidesPerView);
    const barWidth = this.options.progressBarWidth;

    if (this.options.multiProgressMode) {
      // 多进度条模式
      const currentSlide = this.existingSlides[slideIndex];
      const slideProgressFill = currentSlide ? currentSlide.querySelector(this.options.slideProgressFillSelector) : null;

      if (!slideProgressFill) return;

      slideProgressFill.style.width = '0px';
      slideProgressFill.style.transform = `translateX(0px)`;

      const self = this;
      this.progressInterval = setInterval(function() {
        const elapsed = Date.now() - self.progressStartTime;
        const percentage = Math.min(elapsed / delay, 1);

        const currentProgress = barWidth * percentage;
        slideProgressFill.style.transform = `translateX(0px)`;
        slideProgressFill.style.width = `${currentProgress}px`;

        if (percentage >= 1) {
          clearInterval(self.progressInterval);
          slideProgressFill.style.width = `${barWidth}px`;
        }
      }, 16);
    } else {
      // 单进度条模式
      const progressFill = this.swiperEl.querySelector(this.options.progressFillSelector);

      if (!progressFill) return;

      progressFill.style.width = '0px';
      progressFill.style.transform = `translateX(${slideIndex * barWidth}px)`;

      const self = this;
      this.progressInterval = setInterval(function() {
        const elapsed = Date.now() - self.progressStartTime;
        const percentage = Math.min(elapsed / delay, 1);

        const baseOffset = slideIndex * barWidth;
        const currentProgress = barWidth * percentage;
        progressFill.style.transform = `translateX(${baseOffset}px)`;
        progressFill.style.width = `${currentProgress}px`;

        if (percentage >= 1) {
          clearInterval(self.progressInterval);
          progressFill.style.width = `${barWidth}px`;
          
          // 进度条完成后，自动切换到下一个 slide
          if (self.swiper && self.swiper.slideNext) {
            self.swiper.slideNext();
          }
        }
      }, 16);
    }
  };

  // 导出到全局
  window.SwiperProgressBar = SwiperProgressBar;
})();

